import { Form, Text } from '@timelyapp/react';
import React, { useCallback, useEffect, useState } from 'react';
import SimpleSelect from '~components/SimpleSelect';

export const PRESET_INTERVALS = [5, 10, 15, 30, 60];

export const intervalPresetOptions = PRESET_INTERVALS.map(v => ({
  label: `${v} minutes`,
  value: v,
}));

const CUSTOM_SENTINEL = -1;
const MIN_INTERVAL = 5;
const MAX_INTERVAL = 1440;

interface SummaryIntervalSelectProps {
  value: number;
  onChange: (minutes: number) => void;
  disabled?: boolean;
}

export const SummaryIntervalSelect: React.FC<SummaryIntervalSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const isPreset = PRESET_INTERVALS.includes(value);
  const [customMode, setCustomMode] = useState(!isPreset);
  const [customMinutes, setCustomMinutes] = useState(!isPreset ? String(value) : '');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const preset = PRESET_INTERVALS.includes(value);
    setCustomMode(!preset);
    setCustomMinutes(!preset ? String(value) : '');
    setIsDirty(false);
  }, [value]);

  const commitCustomValue = useCallback(
    (raw: string) => {
      const parsed = parseInt(raw, 10);
      if (isNaN(parsed) || parsed < MIN_INTERVAL || parsed > MAX_INTERVAL) return;
      setIsDirty(false);
      if (parsed !== value) onChange(parsed);
    },
    [onChange, value]
  );

  useEffect(() => {
    if (!customMode || !isDirty || !customMinutes) return;
    const timeout = setTimeout(() => commitCustomValue(customMinutes), 1_000);
    return () => clearTimeout(timeout);
  }, [customMode, isDirty, customMinutes, commitCustomValue]);

  const selectOptions = [...intervalPresetOptions, { label: 'Custom', value: CUSTOM_SENTINEL }];

  const selectValue = customMode ? CUSTOM_SENTINEL : value;

  const handleSelectChange = useCallback(
    (v: number) => {
      if (v === CUSTOM_SENTINEL) {
        setCustomMode(true);
        setCustomMinutes('');
        setIsDirty(false);
        return;
      }
      setCustomMode(false);
      setIsDirty(false);
      onChange(v);
    },
    [onChange]
  );

  return (
    <div>
      <SimpleSelect
        value={selectValue}
        options={selectOptions}
        onChange={handleSelectChange}
        disabled={disabled}
        placeholder="Select interval"
        onCreatePlaceholder=""
      />
      {customMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <Form.Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            size="small"
            value={customMinutes}
            placeholder={String(MIN_INTERVAL)}
            style={{ width: 56 }}
            disabled={disabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCustomMinutes(e.target.value);
              setIsDirty(true);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') commitCustomValue(customMinutes);
            }}
            onBlur={() => {
              if (customMinutes) commitCustomValue(customMinutes);
            }}
          />
          <Text size="small">minutes</Text>
          <Text size="small" color="subtle" style={{ marginLeft: 4 }}>
            ({MIN_INTERVAL}–{MAX_INTERVAL})
          </Text>
        </div>
      )}
    </div>
  );
};
